import React, { useState } from 'react';
import { Modal, Input, Radio, Upload, Button, message } from 'antd';
import { 
  PlusOutlined, 
  UploadOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  FileExcelOutlined,
  EditOutlined
} from '@ant-design/icons';
import { uploadImageToOracle } from 'utils/imageUpload';
import adminRequest from 'utils/adminRequest';

const { TextArea } = Input;

const AddQuestionsToSection = ({ 
  visible, 
  onClose, 
  sectionId, 
  sectionName,
  onSuccess 
}) => {
  const [inputMode, setInputMode] = useState('excel');
  const [excelFile, setExcelFile] = useState(null);
  
  // Manual question state
  const [manualQuestions, setManualQuestions] = useState([]);
  const [questionText, setQuestionText] = useState('');
  const [questionImage, setQuestionImage] = useState(null);
  const [options, setOptions] = useState([
    { text: '', image: null },
    { text: '', image: null },
    { text: '', image: null },
    { text: '', image: null }
  ]);
  const [correctOptionIndex, setCorrectOptionIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetState = () => {
    setInputMode('excel');
    setExcelFile(null);
    setQuestionText('');
    setQuestionImage(null);
    setOptions([
      { text: '', image: null },
      { text: '', image: null },
      { text: '', image: null },
      { text: '', image: null }
    ]);
    setCorrectOptionIndex(0);
    setManualQuestions([]);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleExcelChange = info => {
    const file = info.fileList[0]?.originFileObj || null;
    setExcelFile(file);
  };

  const handleQuestionImageChange = info => {
    const file = info.fileList[0]?.originFileObj || null;
    setQuestionImage(file);
  };

  const handleOptionChange = (index, value) => {
    setOptions(prev => {
      const next = [...prev];
      next[index] = { ...next[index], text: value };
      return next;
    });
  };

  const handleOptionImageChange = (index, info) => {
    const file = info.fileList[0]?.originFileObj || null;
    setOptions(prev => {
      const next = [...prev];
      next[index] = { ...next[index], image: file };
      return next;
    });
  };

  const handleAddOption = () => {
    setOptions(prev => [...prev, { text: '', image: null }]);
  };

  const handleRemoveOption = index => {
    setOptions(prev => prev.filter((_, i) => i !== index));
    setCorrectOptionIndex(prev =>
      prev === index ? 0 : prev > index ? prev - 1 : prev
    );
  };

  const handleAddManualQuestion = async () => {
    const trimmedText = questionText.trim();
    const nonEmptyOptions = options.filter(o => o.text.trim() || o.image);

    if (!trimmedText) {
      message.error('Please provide question text');
      return;
    }

    if (nonEmptyOptions.length < 2) {
      message.error('Please provide at least 2 non-empty options');
      return;
    }

    // Validate that the selected correct option is not empty
    const correctOpt = options[correctOptionIndex];
    if (!correctOpt || (!correctOpt.text.trim() && !correctOpt.image)) {
      message.error('The selected correct answer option cannot be empty');
      return;
    }

    // Check for duplicate option texts
    const optionTexts = nonEmptyOptions.map(o => o.text.trim().toLowerCase());
    const duplicates = optionTexts.filter((text, index) => text && optionTexts.indexOf(text) !== index);
    if (duplicates.length > 0) {
      message.error('Two or more options have the same answer text. Please make each option unique.');
      return;
    }

    // Recalculate correct option index relative to non-empty options
    const correctOptOriginal = options[correctOptionIndex];
    const newCorrectIndex = nonEmptyOptions.indexOf(correctOptOriginal);

    try {
      const loadingKey = 'uploading';
      message.loading({ content: 'Uploading images...', key: loadingKey });

      // Upload question image
      let questionImageKey = null;
      if (questionImage instanceof File) {
        const uploadResult = await uploadImageToOracle(questionImage);
        if (uploadResult.success) {
          questionImageKey = uploadResult.objectKey;
        } else {
          throw new Error('Failed to upload question image');
        }
      }

      // Upload option images (only non-empty options)
      const processedOptions = await Promise.all(
        nonEmptyOptions.map(async (opt) => {
          let optionImageKey = null;
          
          if (opt.image instanceof File) {
            const uploadResult = await uploadImageToOracle(opt.image);
            if (uploadResult.success) {
              optionImageKey = uploadResult.objectKey;
            } else {
              throw new Error('Failed to upload option image');
            }
          }
          
          return {
            option_text: opt.text.trim(),
            option_image_key: optionImageKey || ''
          };
        })
      );
  
      const payloadQuestion = {
        question_text: trimmedText,
        question_image_key: questionImageKey || '',
        options: processedOptions,
        correct_opt: newCorrectIndex + 1, // API expects 1-indexed
      };
  
      setManualQuestions(prev => [...prev, payloadQuestion]);
      message.success({ content: 'Question added!', key: loadingKey });
  
      // Reset builder
      setQuestionText('');
      setQuestionImage(null);
      setOptions([
        { text: '', image: null },
        { text: '', image: null },
        { text: '', image: null },
        { text: '', image: null }
      ]);
      setCorrectOptionIndex(0);
    } catch (error) {
      console.error('❌ Error adding question:', error);
      message.error('Failed to add question: ' + error.message);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      if (inputMode === 'excel' && excelFile) {
        // Excel upload
        const formData = new FormData();
        formData.append('section_id', sectionId);
        formData.append('sections[0][section_name]', sectionName);
        formData.append('sections[0][excel]', excelFile);

        const response = await adminRequest.post(
          '/assessment/addQuestionToCustomSection?isFileUpload=true',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        message.success(`${response.data.questions?.length || 0} questions added from Excel!`);
      } else if (inputMode === 'manual' && manualQuestions.length > 0) {
        // Manual questions
        const payload = {
          section_id: sectionId,
          questions: manualQuestions
        };

        const response = await adminRequest.post(
          '/assessment/addQuestionToCustomSection?isFileUpload=false',
          payload
        );

        message.success(`${response.data.questions?.length || 0} questions added!`);
      } else {
        message.error('Please add at least one question');
        setIsSubmitting(false);
        return;
      }

      if (onSuccess) onSuccess();
      handleClose();
    } catch (error) {
      console.error('❌ Error submitting questions:', error);
      message.error(error.response?.data?.error || 'Failed to add questions');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = 
    (inputMode === 'excel' && !excelFile) ||
    (inputMode === 'manual' && manualQuestions.length === 0) ||
    isSubmitting;

  return (
    <Modal
      title={
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 8,
          fontSize: 16,
          fontWeight: 600 
        }}>
          <PlusOutlined style={{ fontSize: 18, color: '#1890ff' }} />
          Add Questions to {sectionName}
        </div>
      }
      open={visible}
      onCancel={handleClose}
      width={720}
      footer={[
        <Button 
          key="cancel" 
          onClick={handleClose}
          disabled={isSubmitting}
          icon={<CloseOutlined />}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          loading={isSubmitting}
          icon={<CheckCircleOutlined />}
        >
          Add Questions
        </Button>,
      ]}
      destroyOnClose
    >
      <div style={{ padding: '16px 0' }}>
        {/* Input Mode Toggle */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ 
            display: 'block',
            marginBottom: 8, 
            fontSize: 14, 
            fontWeight: 600,
            color: '#262626'
          }}>
            Question Input Method
          </label>
          <Radio.Group
            value={inputMode}
            onChange={e => setInputMode(e.target.value)}
            buttonStyle="solid"
            style={{ width: '100%' }}
          >
            <Radio.Button 
              value="excel" 
              style={{ 
                width: '50%', 
                textAlign: 'center',
                height: 40
              }}
            >
              <FileExcelOutlined /> Excel Upload
            </Radio.Button>
            <Radio.Button 
              value="manual" 
              style={{ 
                width: '50%', 
                textAlign: 'center',
                height: 40
              }}
            >
              <EditOutlined /> Manual Entry
            </Radio.Button>
          </Radio.Group>
        </div>

        {/* Excel Upload UI */}
        {inputMode === 'excel' && (
          <div 
            style={{ 
              padding: 20,
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid #e8e8e8'
            }}
          >
            <div style={{ 
              marginBottom: 16, 
              fontSize: 14, 
              fontWeight: 600,
              color: '#262626'
            }}>
              Upload Excel File
            </div>
            <Upload
              beforeUpload={() => false}
              onChange={handleExcelChange}
              maxCount={1}
              accept=".xlsx,.xls"
            >
              <Button 
                icon={<UploadOutlined />}
                block
              >
                Select Excel File
              </Button>
            </Upload>
            {excelFile && (
              <div style={{ 
                marginTop: 12, 
                padding: 12,
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 6,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <span style={{ color: '#389e0d' }}>{excelFile.name}</span>
              </div>
            )}
          </div>
        )}

        {/* Manual Entry UI - Same as CustomAssessmentSectionCreator */}
        {inputMode === 'manual' && (
          <div 
            style={{ 
              padding: 20,
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid #e8e8e8',
              maxHeight: 450,
              overflowY: 'auto'
            }}
          >
            {/* Question Text */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ 
                display: 'block',
                marginBottom: 8, 
                fontSize: 14, 
                fontWeight: 600,
                color: '#262626'
              }}>
                Question Text
              </label>
              <TextArea
                rows={3}
                placeholder="Enter your question"
                value={questionText}
                onChange={e => setQuestionText(e.target.value)}
              />
            </div>

            {/* Question Image */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ 
                display: 'block',
                marginBottom: 8, 
                fontSize: 14, 
                fontWeight: 600,
                color: '#262626'
              }}>
                Question Image <span style={{ color: '#8c8c8c', fontWeight: 400 }}>(Optional)</span>
              </label>
              <Upload
                beforeUpload={() => false}
                onChange={handleQuestionImageChange}
                maxCount={1}
                listType="picture-card"
                accept="image/*"
                fileList={questionImage ? [{
                  uid: '-question-img',
                  name: questionImage.name,
                  status: 'done',
                  url: URL.createObjectURL(questionImage)
                }] : []}
              >
                {!questionImage && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8, fontSize: 12 }}>Upload</div>
                  </div>
                )}
              </Upload>
            </div>

            {/* Options */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ 
                display: 'block',
                marginBottom: 8, 
                fontSize: 14, 
                fontWeight: 600,
                color: '#262626'
              }}>
                Answer Options
              </label>
              <Radio.Group
                value={correctOptionIndex}
                onChange={e => setCorrectOptionIndex(e.target.value)}
                style={{ width: '100%' }}
              >
                {options.map((opt, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: 12,
                      padding: 12,
                      background: '#fff',
                      border: correctOptionIndex === index 
                        ? '1px solid #1890ff' 
                        : '1px solid #e8e8e8',
                      borderRadius: 6,
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 8,
                      marginBottom: 8 
                    }}>
                      <Radio value={index}>
                        <span style={{ fontWeight: 500, fontSize: 13 }}>
                          Option {String.fromCharCode(65 + index)}
                        </span>
                      </Radio>
                      {options.length > 2 && (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() => handleRemoveOption(index)}
                          style={{ marginLeft: 'auto' }}
                        />
                      )}
                    </div>
                    <Input
                      placeholder={`Enter option ${String.fromCharCode(65 + index)}`}
                      value={opt.text}
                      onChange={e => handleOptionChange(index, e.target.value)}
                      style={{ marginBottom: 8 }}
                    />
                    
                    {/* Option Image */}
                    <div>
                      <div style={{ 
                        fontSize: 12, 
                        color: '#8c8c8c',
                        marginBottom: 6
                      }}>
                        Image <span style={{ fontWeight: 400 }}>(Optional)</span>
                      </div>
                      <Upload
                        beforeUpload={() => false}
                        onChange={info => handleOptionImageChange(index, info)}
                        maxCount={1}
                        listType="picture-card"
                        accept="image/*"
                        fileList={opt.image ? [{
                          uid: `-opt-${index}`,
                          name: opt.image.name,
                          status: 'done',
                          url: URL.createObjectURL(opt.image)
                        }] : []}
                      >
                        {!opt.image && (
                          <div style={{ fontSize: 11 }}>
                            <PlusOutlined />
                            <div style={{ marginTop: 4 }}>Upload</div>
                          </div>
                        )}
                      </Upload>
                    </div>
                  </div>
                ))}
              </Radio.Group>

              <Button 
                type="dashed" 
                onClick={handleAddOption}
                icon={<PlusOutlined />}
                block
                style={{ marginTop: 8 }}
              >
                Add Option
              </Button>
            </div>

            {/* Add Question Button */}
            <Button 
              type="primary" 
              onClick={handleAddManualQuestion}
              icon={<CheckCircleOutlined />}
              block
            >
              Add Question
            </Button>

            {/* Summary */}
            {manualQuestions.length > 0 && (
              <div style={{ 
                marginTop: 16, 
                padding: 12,
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 6,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <span style={{ color: '#389e0d' }}>
                  {manualQuestions.length} question{manualQuestions.length > 1 ? 's' : ''} ready to add
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AddQuestionsToSection;